import React from 'react'
import { Link } from 'react-router-dom'
import { Atom, Activity, FlaskConical, Rocket } from 'lucide-react'
import LanguageSwitcher from './LanguageSwitcher'
import { useLocale } from '../i18n/LocaleContext'

interface LandingPageProps {
  isAuthenticated: boolean
  logoSrc: string
  heroImageSrc: string
}

const LandingPage: React.FC<LandingPageProps> = ({ isAuthenticated, logoSrc, heroImageSrc }) => {
  const { t } = useLocale()

  return (
    <main className="lp-page">
      <div className="lp-lang">
        <LanguageSwitcher />
      </div>

      <section className="lp-hero">
        <div className="lp-hero-grid">
          <div>
            <p className="lp-kicker">{t('landing.kicker')}</p>
            <h1>
              {t('landing.title')}
              <span>{t('landing.tagline')}</span>
            </h1>
            <p className="lp-subtitle">{t('landing.subtitle')}</p>
            <div className="lp-actions">
              <Link className="btn btn-primary" to={isAuthenticated ? '/simulation' : '/login'}>
                {isAuthenticated ? t('landing.cta.enter') : t('landing.cta.login')}
              </Link>
              <a className="btn btn-secondary" href="#lp-about">
                {t('landing.cta.learn')}
              </a>
            </div>
            <div className="lp-badges">
              <span><Rocket size={14} /> {t('landing.badge.taichi')}</span>
              <span><FlaskConical size={14} /> {t('landing.badge.pubchem')}</span>
              <span><Activity size={14} /> {t('landing.badge.streaming')}</span>
            </div>
          </div>
          <div className="lp-hero-media">
            <img src={heroImageSrc} alt={t('landing.hero.alt')} />
          </div>
        </div>
      </section>

      <section id="lp-about" className="lp-section">
        <h2>{t('landing.about.title')}</h2>
        <p className="lp-section-subtitle">{t('landing.about.lead')}</p>

        <div className="lp-prose">
          <article className="lp-prose-block">
            <h3>{t('landing.about.what.title')}</h3>
            <p>{t('landing.about.what.body')}</p>
          </article>
          <article className="lp-prose-block">
            <h3>{t('landing.about.origin.title')}</h3>
            <p>{t('landing.about.origin.body')}</p>
          </article>
          <article className="lp-prose-block">
            <h3>{t('landing.about.solves.title')}</h3>
            <p>{t('landing.about.solves.body')}</p>
          </article>
          <article className="lp-prose-block">
            <h3>{t('landing.about.future.title')}</h3>
            <p>{t('landing.about.future.body')}</p>
          </article>
          <article className="lp-prose-block">
            <h3>{t('landing.about.why.title')}</h3>
            <p>{t('landing.about.why.body')}</p>
          </article>
        </div>

        <h2 className="lp-section-heading-follow">{t('landing.why.title')}</h2>
        <p className="lp-section-subtitle">{t('landing.why.subtitle')}</p>
        <div className="lp-cards">
          <article className="lp-card">
            <h3><Atom size={16} /> {t('landing.card.thermo.title')}</h3>
            <p>{t('landing.card.thermo.body')}</p>
          </article>
          <article className="lp-card">
            <h3><FlaskConical size={16} /> {t('landing.card.discovery.title')}</h3>
            <p>{t('landing.card.discovery.body')}</p>
          </article>
          <article className="lp-card">
            <h3><Rocket size={16} /> {t('landing.card.gpu.title')}</h3>
            <p>{t('landing.card.gpu.body')}</p>
          </article>
        </div>
      </section>

      <section className="lp-section lp-section-compact">
        <h2>{t('landing.materials.title')}</h2>
        <div className="lp-links">
          <a href="https://github.com/ProhunterPL/live2.0" target="_blank" rel="noreferrer">
            {t('landing.materials.github')}
          </a>
          <a href="https://doi.org/10.5281/zenodo.17814793" target="_blank" rel="noreferrer">
            {t('landing.materials.zenodo')}
          </a>
          <a href="mailto:klawikowski@klawikowski.pl">{t('landing.materials.contact')}</a>
        </div>
      </section>

      <footer className="lp-footer">
        <img src={logoSrc} alt={t('landing.logo.alt')} />
        <span>Live 2.0</span>
      </footer>
    </main>
  )
}

export default LandingPage
